import CloseIcon from '@mui/icons-material/Close';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { Chip, Stack, Typography, useTheme } from '@mui/material';
import {
  ConversationChannelType,
  ConversationParticipantEntity,
} from '@provision/conversation-types';
import { MouseEvent, useCallback, useState } from 'react';
import { Tooltip } from 'src/components';
import { ParticipantChipMenu } from './participant-chip-menu-items';
import { ParticipantSelectorItem } from './types';

interface ParticipantChipItemProps {
  participant: ParticipantSelectorItem;
  isInvalid?: boolean;
  readOnly?: boolean;
  onDelete?: () => void;
  onAddEmail: (data: ParticipantSelectorItem, email: string) => void;
  onRemoveEmail: (data: ParticipantSelectorItem, email: string) => void;
}

export const ParticipantChipItem = ({
  participant,
  isInvalid = false,
  readOnly = false,
  onDelete,
  onAddEmail,
  onRemoveEmail,
}: ParticipantChipItemProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const theme = useTheme();

  const handleChipClick = (event: MouseEvent<HTMLElement>) => {
    if (participant.participantEntity !== ConversationParticipantEntity.Unknown) {
      setAnchorEl(event.currentTarget);
    }
  };

  const onMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const label = participant.fullName || participant.participantIdentity;

  const emails = participant.channels?.filter?.(
    (channel) =>
      participant.participantEntity !== ConversationParticipantEntity.Unknown &&
      (channel.conversationChannelTypeId as ConversationChannelType) ===
        ConversationChannelType.Email,
  );

  const emailsCount = emails?.length || 0;

  const tooltipTitle = (
    <Typography
      variant="subtitle2"
      sx={{ whiteSpace: 'preserve-breaks', fontSize: '0.75rem' }}
    >
      {[label, ...emails.map((it) => it.externalIdentifier)].join('\n')}
    </Typography>
  );

  return (
    <>
      <Chip
        label={
          <Tooltip title={tooltipTitle} enterDelay={1000}>
            <Stack direction="row" gap="0.25rem" alignItems="center">
              <Typography
                variant="body2"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
                overflow="hidden"
                sx={{
                  fontSize: '0.75rem',
                  lineHeight: '1rem',
                  ...(isInvalid && { color: theme.palette.error.main }),
                }}
              >
                {label}
              </Typography>

              {emailsCount > 0 && (
                <Stack direction="row" gap="0.25rem" alignItems="center">
                  <EmailOutlinedIcon
                    sx={{
                      fontSize: '1rem',
                      color: theme.palette.custom.OV.sys.secondary,
                    }}
                  />
                  {emailsCount > 1 && (
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '0.75rem',
                        lineHeight: '1rem',
                        marginRight: '0.25rem',
                        color: theme.palette.custom.OV.sys.secondary,
                      }}
                    >
                      {emailsCount}
                    </Typography>
                  )}
                </Stack>
              )}
            </Stack>
          </Tooltip>
        }
        onClick={readOnly ? undefined : handleChipClick}
        onDelete={readOnly ? undefined : onDelete}
        deleteIcon={
          onDelete && !readOnly ? (
            <Tooltip title="Remove" enterDelay={3000}>
              <CloseIcon />
            </Tooltip>
          ) : undefined
        }
        sx={{
          minWidth: '4rem',
          height: 'auto',
          padding: '0.125rem 0.2rem 0.125rem 0.375rem',
          cursor: 'pointer',
          fontSize: '0.75rem',
          borderRadius: '6px',
          backgroundColor: theme.palette.custom.OV.sys.surfaceContainer,
          ...(isInvalid && {
            border: `1px solid ${theme.palette.error.main}`,
          }),
          ...(!readOnly && {
            '&:hover': {
              backgroundColor:
                participant.participantEntity !== ConversationParticipantEntity.Unknown
                  ? theme.palette.custom.OV.sys.surfaceVariant
                  : undefined,
            },
          }),

          '& .MuiChip-label': {
            paddingLeft: '0',
            paddingRight: '0.1rem',
          },
          '& .MuiChip-deleteIcon': {
            margin: 0,
            fontSize: '1rem',
          },
        }}
      />

      {!readOnly && (
        <ParticipantChipMenu
          anchorEl={anchorEl}
          onClose={onMenuClose}
          onAddEmail={onAddEmail}
          onRemoveEmail={onRemoveEmail}
          participant={participant}
        />
      )}
    </>
  );
};
